import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coversApi, settingsApi, ordersApi, uploadToSignedUrl, editorResourcesApi, type CoverCategory, type AppSettings } from '@/lib/api';
import { AdminLayout } from '@/components/AdminLayout';
import { SafeSvg } from '@/components/SafeSvg';
import { Button } from '@/components/ui/Button';
import { initPixels, trackEvent, pixelStatus } from '@/lib/pixels';
import { generateAssemblyTemplatePdf, computeTemplateLayout, computeVinylSizeMm, withDerivedVinyl, DEFAULT_PRINT_SETTINGS, type PrintSettings } from '@/lib/assemblyTemplate';
import { useT } from '@/lib/i18n';
import { BASE_PAGES_PER_ALBUM } from '@/lib/orderPricing';
import { CleanupResultModal } from './settings/CleanupResultModal';
import { AppearanceSwitch } from '@/components/appShell/AppearanceSwitch';
import { MyDevicesSection, TeamAccessSection, LoginLogSection } from './settings/LoginSecuritySections';
import { AiProvidersSection, AiTasksSection, AiReplyRouterSection, AiCostEstimateSection } from './settings/AiProvidersSections';
import { CouponsSettingsSection } from './coupons/CouponsSettingsSection';
import {
  formatFreed, freedDetail, didSomething, remainingNote, pdfSkipNote,
  type CleanupOutcome,
} from './settings/cleanupResult';

// ─── Redesign 2026-06-10 — clustered settings with sticky section nav ───────
// 10 flat cards → 6 task clusters. Left nav (desktop) / chip bar (mobile) with
// scrollspy; rarely-touched sections collapsed to one elegant row. Anchors,
// not tabs: ⌘F still works, nothing hidden, zero lost state.

/** Collapsible shell for low-traffic sections. Closed = slim summary row;
 *  open = the untouched original section card (own header included). */
function SettingBlock({
  label,
  summary,
  defaultOpen = false,
  children,
}: {
  label: string;
  summary?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(defaultOpen);
  if (open) {
    return (
      <div className="adm-rise">
        {!defaultOpen && (
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mb-1 text-[11px] font-semibold flex items-center gap-1"
            style={{ color: 'var(--color-adm-gold-ink)' }}
          >
            <svg className="w-3 h-3 rotate-90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {t('Réduire {label}', { label })}
          </button>
        )}
        {children}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className="w-full bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3 text-left hover:border-[#C9A24A66] hover:-translate-y-px transition-all cursor-pointer"
    >
      <span className="text-sm font-semibold text-gray-800">{label}</span>
      {summary && <span className="text-xs text-gray-400 truncate">{summary}</span>}
      <svg className="w-4 h-4 ml-auto shrink-0 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  );
}

export default function AdminSettingsPage() {
  const { t } = useT();
  useEffect(() => {
    document.title = 'Paramètres — Admin Storiz';
  }, []);

  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
  });

  const { data: categoriesData } = useQuery({
    queryKey: ['cover-categories'],
    queryFn: () => coversApi.categories(),
  });

  const settings = settingsData?.settings;
  const categories = categoriesData?.categories ?? [];

  // Clusters: id → anchor, label, items. Item `el` keeps every existing
  // section component untouched; `open` marks the always-expanded ones.
  const clusters: Array<{
    id: string; icon: string; label: string;
    items: Array<{ id: string; label: string; summary?: string; open?: boolean; el: React.ReactNode }>;
  }> = [
    {
      id: 'prix', icon: '💰', label: t('Album & Prix'),
      items: [
        { id: 'tarification', label: t('Album & Tarification'), open: true, el: <AlbumSettingsSection settings={settings} /> },
        { id: 'cout-revient', label: t('Coût de revient'), summary: t('Paramètres matières / impression'), el: <CostParamsSection settings={settings} /> },
        { id: 'funnel', label: t('Tunnel de commande'), summary: t('Classique vs simplifié'), el: <FunnelSection settings={settings} /> },
        { id: 'assignation', label: t('File d’attente des agents'), summary: t('Limite de commandes et remise en file'), el: <AssignmentSection settings={settings} /> },
      ],
    },
    {
      id: 'impression', icon: '🖨', label: t('Impression'),
      items: [
        { id: 'print', label: t("Paramètres d'impression"), open: true, el: <PrintSettingsSection settings={settings} /> },
        { id: 'fiche', label: t('Fiche de montage'), summary: t("PDF universel pour l'atelier"), el: <AssemblyTemplateSection settings={settings} /> },
        { id: 'imprimeur', label: t('Imprimeur'), summary: t('Contact & envoi'), el: <PrinterSection settings={settings} /> },
      ],
    },
    {
      id: 'livraison', icon: '🚚', label: t('Livraison'),
      items: [
        { id: 'ameex', label: t('AMEEX par défaut'), summary: t('Ville, COD, étiquettes'), el: <AmeexDefaultsSection settings={settings} /> },
      ],
    },
    {
      id: 'marque', icon: '✨', label: t('Marque & Studio'),
      items: [
        { id: 'branding', label: t('Identité de marque'), open: true, el: <BrandingSection settings={settings} /> },
        { id: 'categories', label: t('Catégories de couvertures'), summary: categories.length > 1 ? t('{n} catégories', { n: categories.length }) : t('{n} catégorie', { n: categories.length }), el: <CategoriesSection categories={categories} /> },
        { id: 'ressources', label: t("Ressources de l'éditeur"), summary: t('Polices · Icônes'), el: <EditorResourcesSection /> },
      ],
    },
    {
      id: 'marketing', icon: '📈', label: t('Marketing'),
      items: [
        { id: 'pixels', label: t('Pixels de tracking'), summary: t('Facebook · TikTok · GA4 · GTM · Clarity'), el: <PixelsSection settings={settings} /> },
        // Coupons (2026-10-28): 3 switches, duplex flip + offset, alignment test.
        { id: 'coupons', label: t('Coupons'), summary: settings?.coupons_enabled || settings?.coupon_redeem_enabled ? t('Allumés') : t('Éteints'), el: <CouponsSettingsSection settings={settings} /> },
      ],
    },
    {
      id: 'systeme', icon: '⚙️', label: t('Système'),
      items: [
        { id: 'apparence', label: t('Apparence'), open: true, el: <AppearanceSection /> },
        { id: 'retention', label: t('Rétention des fichiers'), summary: t('Nettoyage automatique'), el: <FileRetentionSection settings={settings} /> },
        { id: 'blocage', label: t('Sécurité & Blocage'), summary: t('Clients bannis · limite par IP'), el: <BlocklistSection settings={settings} /> },
      ],
    },
    {
      // OpenRouter + Claude: keys, budgets, task routes, WhatsApp easy/complex,
      // cost estimate (owner, 2026-10-23) — settings/AiProvidersSections.tsx.
      id: 'ia', icon: '🤖', label: t('Fournisseurs IA'),
      items: [
        { id: 'ia-fournisseurs', label: t('Fournisseurs'), open: true, el: <AiProvidersSection /> },
        { id: 'ia-taches', label: t('Tâches'), summary: t('Quel modèle pour chaque tâche IA'), el: <AiTasksSection /> },
        { id: 'ia-whatsapp', label: t('Réponses WhatsApp : facile / complexe'), summary: t('Haiku / Sonnet et leurs règles'), el: <AiReplyRouterSection /> },
        { id: 'ia-estimation', label: t('Estimation des coûts'), summary: t('Chaque tâche sur chaque modèle Claude'), el: <AiCostEstimateSection /> },
      ],
    },
    {
      // Login with Google, allowlist only (owner, 2026-10-21) — settings/LoginSecuritySections.tsx.
      id: 'connexion', icon: '🔐', label: t('Connexion & sécurité'),
      items: [
        { id: 'appareils', label: t('Mes appareils'), open: true, el: <MyDevicesSection /> },
        { id: 'acces', label: t('Accès équipe'), summary: t('Emails autorisés · mot de passe de secours'), el: <TeamAccessSection /> },
        { id: 'journal-connexions', label: t('Journal des connexions'), summary: t('Qui · quand · appareil · IP'), el: <LoginLogSection /> },
      ],
    },
  ];

  // Scrollspy — highlight the nav item whose section is nearest the top.
  const [activeId, setActiveId] = useState('tarification');
  useEffect(() => {
    const ids = clusters.flatMap((c) => c.items.map((i) => i.id));
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        let best = ids[0];
        let bestTop = -Infinity;
        for (const id of ids) {
          const el = document.getElementById(`set-${id}`);
          if (!el) continue;
          const top = el.getBoundingClientRect().top;
          if (top <= 140 && top > bestTop) { bestTop = top; best = id; }
        }
        setActiveId(best);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jump = (id: string) => {
    document.getElementById(`set-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const nav = (horizontal: boolean) => (
    horizontal ? (
      <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
        {clusters.flatMap((c) => c.items).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => jump(item.id)}
            className="shrink-0 px-3.5 py-2 rounded-full text-xs font-semibold bg-white border transition-colors"
            style={activeId === item.id
              ? { borderColor: 'rgba(201,162,74,.5)', background: 'rgba(201,162,74,.1)', color: 'var(--color-adm-gold-ink)' }
              : { borderColor: 'var(--color-adm-line)', color: 'var(--color-adm-muted)' }}
          >
            {item.label}
          </button>
        ))}
      </div>
    ) : (
      <nav className="space-y-4">
        {clusters.map((c) => (
          <div key={c.id}>
            <p className="px-3 pb-1.5 text-[10px] font-bold tracking-[.09em] uppercase text-gray-400">
              {c.icon}&ensp;{c.label}
            </p>
            <div className="space-y-0.5">
              {c.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => jump(item.id)}
                  className="w-full text-left px-3 py-[7px] rounded-lg text-[12.5px] font-medium transition-colors cursor-pointer"
                  style={activeId === item.id
                    ? { background: 'rgba(201,162,74,.1)', color: 'var(--color-adm-gold-ink)', fontWeight: 700, boxShadow: 'inset 2px 0 0 #C9A24A' }
                    : { color: 'var(--color-adm-muted)' }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </nav>
    )
  );

  return (
    <AdminLayout title={t('Paramètres')} subtitle={t('Réglages du studio Storiz')}>
      {/* Mobile chip nav — sticky under the hamburger header */}
      <div
        className="lg:hidden sticky z-20 -mx-4 px-4 py-2.5 -mt-5 mb-4 border-b"
        // `top` uses the shared token, same as the order page. The old
        // top-[57px] was both 8px short of the real 65px app header AND wrong
        // between 768–1023px, where this bar still shows (lg:hidden) but the app
        // header is already gone (md:hidden) — it reserved 57px against nothing.
        style={{
          top: 'var(--adm-header-h)',
          // = rgba(250,246,236,.95) in light: the canvas at 95 %, whatever the mode.
          background: 'color-mix(in srgb, var(--color-adm-canvas) 95%, transparent)',
          backdropFilter: 'blur(8px)',
          borderColor: 'rgba(201,162,74,.16)',
        }}
      >
        {nav(true)}
      </div>

      <div className="max-w-6xl grid grid-cols-1 lg:grid-cols-[225px_minmax(0,1fr)] gap-7 items-start">
        {/* Desktop sticky nav */}
        <div className="hidden lg:block sticky top-[100px]">{nav(false)}</div>

        {/* Content — clusters in order */}
        <div className="space-y-7 min-w-0">
          {clusters.map((c, ci) => (
            <div key={c.id} className="adm-rise" style={{ animationDelay: `${ci * 45}ms` }}>
              <div className="flex items-center gap-2.5 mb-3">
                <h2
                  className="text-[15.5px] font-semibold text-[#1F2937] dark:text-adm-ink"
                  style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: '.01em' }}
                >
                  {c.icon}&ensp;{c.label}
                </h2>
                <div className="flex-1 h-px" style={{ background: 'rgba(201,162,74,.22)' }} />
              </div>
              <div className="space-y-3">
                {c.items.map((item) => (
                  <div key={item.id} id={`set-${item.id}`} className="scroll-mt-28 lg:scroll-mt-24">
                    <SettingBlock label={item.label} summary={item.summary} defaultOpen={!!item.open}>
                      {item.el}
                    </SettingBlock>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

// ─── Pixels (Facebook, TikTok, GA4, GTM) ──────────────────────────────────────
// Paste the SAME pixel IDs configured in the Shopify theme so events on
// app.storiz.ma feed the same Ads Manager / Events Manager / GA dashboards
// as the storefront. Empty input = pixel disabled (no script injected).
function PixelsSection({ settings }: { settings: AppSettings | undefined }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [fb, setFb] = useState(settings?.facebook_pixel_id ?? '');
  const [tiktok, setTiktok] = useState(settings?.tiktok_pixel_id ?? '');
  const [ga4, setGa4] = useState(settings?.ga4_measurement_id ?? '');
  const [gtm, setGtm] = useState(settings?.gtm_container_id ?? '');
  const [clarity, setClarity] = useState(settings?.clarity_project_id ?? '');
  const [openaiPixel, setOpenaiPixel] = useState(settings?.openai_pixel_id ?? '');
  const [saved, setSaved] = useState(false);
  const [testToast, setTestToast] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    setFb(settings.facebook_pixel_id ?? '');
    setTiktok(settings.tiktok_pixel_id ?? '');
    setGa4(settings.ga4_measurement_id ?? '');
    setGtm(settings.gtm_container_id ?? '');
    setClarity(settings.clarity_project_id ?? '');
    setOpenaiPixel(settings.openai_pixel_id ?? '');
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      settingsApi.update({
        facebook_pixel_id: fb.trim() || null,
        tiktok_pixel_id: tiktok.trim() || null,
        ga4_measurement_id: ga4.trim() || null,
        gtm_container_id: gtm.trim() || null,
        clarity_project_id: clarity.trim() || null,
        openai_pixel_id: openaiPixel.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['app-settings-pixels'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      // Re-init pixels in this tab so the admin can immediately fire the
      // "Tester" buttons without a hard refresh.
      initPixels({
        facebookPixelId: fb.trim() || null,
        tiktokPixelId: tiktok.trim() || null,
        ga4MeasurementId: ga4.trim() || null,
        gtmContainerId: gtm.trim() || null,
        clarityProjectId: clarity.trim() || null,
      });
    },
  });

  function handleTest(source: 'fb' | 'tiktok' | 'ga4' | 'clarity' | 'all') {
    const status = pixelStatus();
    const liveMap = { fb: status.fb, tiktok: status.tiktok, ga4: status.ga4, clarity: status.clarity, all: true };
    if (!liveMap[source]) {
      setTestToast(t('{source} non chargé. Enregistrez et rafraîchissez d’abord.', { source: source.toUpperCase() }));
      setTimeout(() => setTestToast(null), 4000);
      return;
    }
    trackEvent(`storiz_test_${source}`, { source, ts: new Date().toISOString() });
    setTestToast(t('Événement « storiz_test_{source} » envoyé. Vérifiez dans Events Manager / DevTools console.', { source }));
    setTimeout(() => setTestToast(null), 4000);
  }

  return (
    <Section
      title={t('Pixels de tracking')}
      description={t("Collez les IDs des pixels configurés dans Shopify pour suivre l'activité dans l'app (Facebook, TikTok, Google) dans les mêmes tableaux de bord.")}
    >
      <div className="space-y-4">
        <PixelField
          label={t('Facebook / Meta Pixel')}
          placeholder="123456789012345"
          help={t('Ads Manager → Events Manager → ID du pixel')}
          value={fb}
          onChange={setFb}
          onTest={() => handleTest('fb')}
        />
        <PixelField
          label={t('TikTok Pixel')}
          placeholder="C123ABC456DEF"
          help={t('TikTok Events Manager → ID du pixel')}
          value={tiktok}
          onChange={setTiktok}
          onTest={() => handleTest('tiktok')}
        />
        <PixelField
          label={t('Google Analytics 4')}
          placeholder="G-XXXXXXXXXX"
          help={t('GA Admin → Data Streams → ID de mesure')}
          value={ga4}
          onChange={setGa4}
          onTest={() => handleTest('ga4')}
        />
        <PixelField
          label={t('Google Tag Manager (optionnel)')}
          placeholder="GTM-XXXXXXX"
          help={t('Alternative à GA4 direct. Si vous gérez les tags dans GTM.')}
          value={gtm}
          onChange={setGtm}
        />
        <PixelField
          label={t('OpenAI / ChatGPT')}
          placeholder="ELjo1NLuZgzibG2x3Auq1P"
          // ⚠️ NO TEST BUTTON. The others fire a test event through the worker;
          // this pixel lives entirely in the Shopify theme, so there is nothing
          // here to fire. The help text says where the live copy is, because a
          // field that looks like the source of truth and is not is worse than
          // no field at all.
          help={t('Pixel OpenAI (visiteurs venant de ChatGPT). ⚠️ Copie de référence : le pixel actif est dans le thème Shopify → Réglages → Pixels & Analytics.')}
          value={openaiPixel}
          onChange={setOpenaiPixel}
        />
        <PixelField
          label={t('Microsoft Clarity')}
          placeholder="wp8zhacrzb"
          help={t('Clarity Project → Settings → Overview → Project ID. Active session replays + heatmaps.')}
          value={clarity}
          onChange={setClarity}
          onTest={() => handleTest('clarity')}
        />
      </div>

      <div className="flex items-center gap-3 pt-4 mt-4 border-t border-gray-100 flex-wrap">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
          {t('Enregistrer')}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => handleTest('all')}>
          {t('🧪 Envoyer un événement test')}
        </Button>
        {saved && <span className="text-xs text-green-600 font-medium">{t('Sauvegardé !')}</span>}
        {testToast && <span className="text-xs text-gray-600">{testToast}</span>}
      </div>

      <p className="text-xs text-gray-400 mt-3">
        {t('Les scripts se chargent au prochain rechargement de la page pour les nouveaux IDs.')}{' '}
        {t('Vérifiez le pixel avec l’extension Chrome')} <strong>Meta Pixel Helper</strong> /{' '}
        <strong>TikTok Pixel Helper</strong>, {t('ou regardez la console DevTools (logs')} <code>[pixels]</code>).
      </p>
    </Section>
  );
}

function PixelField({
  label,
  placeholder,
  help,
  value,
  onChange,
  onTest,
}: {
  label: string;
  placeholder: string;
  help: string;
  value: string;
  onChange: (v: string) => void;
  onTest?: () => void;
}) {
  const { t } = useT();
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
        {onTest && (
          <Button size="sm" variant="secondary" onClick={onTest} disabled={!value.trim()}>
            {t('Tester')}
          </Button>
        )}
      </div>
      <p className="text-xs text-gray-400 mt-1">{help}</p>
    </div>
  );
}

// ─── Branding (logo + intro/outro) ────────────────────────────────────────────
function BrandingSection({
  settings,
}: {
  settings: ReturnType<typeof settingsApi.get> extends Promise<{ settings: infer T }> ? T | undefined : never;
}) {
  const { t } = useT();
  const queryClient = useQueryClient();

  const updateSettings = useMutation({
    mutationFn: (data: Record<string, unknown>) => settingsApi.update(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });

  async function handleFileUpload(assetType: string, file: File) {
    const signed = await settingsApi.uploadAsset(file.name, assetType);
    await uploadToSignedUrl(signed.upload_url, file);
    const field = assetType === 'logo' ? 'logo_url' : `${assetType}_url`;
    updateSettings.mutate({ [field]: signed.public_url });
  }

  return (
    <Section title={t('Identité de marque')} description={t('Logo et pages fixes qui apparaissent dans chaque album')}>
      <div className="grid sm:grid-cols-2 gap-4">
        <AssetUploader
          label={t('Logo Storiz')}
          description={t('PNG ou SVG, placé sur la couverture + tranche')}
          currentUrl={settings?.logo_url ?? null}
          accept="image/png,image/svg+xml,image/jpeg"
          onUpload={(file) => handleFileUpload('logo', file)}
          onClear={() => updateSettings.mutate({ logo_url: null })}
        />
        <AssetUploader
          label={t("Page d'intro")}
          description={t("Première page de l'album, ajoutée automatiquement")}
          currentUrl={settings?.intro_page_1_url ?? null}
          accept="image/*,application/pdf"
          onUpload={(file) => handleFileUpload('intro_page_1', file)}
          onClear={() => updateSettings.mutate({ intro_page_1_url: null })}
        />
        <AssetUploader
          label={t("Page d'outro")}
          description={t("Dernière page de l'album, ajoutée automatiquement")}
          currentUrl={settings?.outro_page_2_url ?? null}
          accept="image/*,application/pdf"
          onUpload={(file) => handleFileUpload('outro_page_2', file)}
          onClear={() => updateSettings.mutate({ outro_page_2_url: null })}
        />
      </div>
    </Section>
  );
}

function AssetUploader({
  label,
  description,
  currentUrl,
  accept,
  onUpload,
  onClear,
}: {
  label: string;
  description: string;
  currentUrl: string | null;
  accept: string;
  onUpload: (file: File) => Promise<void>;
  onClear: () => void;
}) {
  const { t } = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await onUpload(file);
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const isImage = currentUrl && /\.(png|jpg|jpeg|svg|webp)(\?|$)/i.test(currentUrl);

  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
        {currentUrl && (
          <button onClick={onClear} className="text-xs text-red-600 hover:underline">
            {t('Retirer')}
          </button>
        )}
      </div>

      {currentUrl && isImage && (
        <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
          <img src={currentUrl} alt="" className="max-h-16 mx-auto object-contain" />
        </div>
      )}

      {currentUrl && !isImage && (
        <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-2 text-sm text-gray-600">
          📄 <a href={currentUrl} target="_blank" rel="noreferrer" className="truncate hover:underline">
            {t('Voir le fichier')}
          </a>
        </div>
      )}

      <input ref={inputRef} type="file" accept={accept} onChange={handleFile} className="hidden" />
      <Button
        variant="secondary"
        size="sm"
        className="w-full"
        onClick={() => inputRef.current?.click()}
        loading={uploading}
      >
        {currentUrl ? t('Remplacer') : t('Téléverser')}
      </Button>
    </div>
  );
}

// ─── Categories CRUD ──────────────────────────────────────────────────────────

function CategoriesSection({ categories }: { categories: CoverCategory[] }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [newSlug, setNewSlug] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newIcon, setNewIcon] = useState('');

  const create = useMutation({
    mutationFn: () =>
      coversApi.createCategory({
        slug: newSlug.trim().toLowerCase(),
        label_fr: newLabel.trim(),
        icon: newIcon.trim() || undefined,
        sort_order: categories.length + 1,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cover-categories'] });
      setNewSlug('');
      setNewLabel('');
      setNewIcon('');
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => coversApi.deleteCategory(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cover-categories'] }),
  });

  return (
    <Section
      title={t('Catégories de couvertures')}
      description={t('Organisez vos couvertures par thèmes (Voyage, Anniversaire...)')}
    >
      <div className="space-y-2 mb-4">
        {categories.map((cat) => (
          <div
            key={cat.id}
            className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg"
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">{cat.icon}</span>
              <div>
                <p className="text-sm font-medium">{cat.label_fr}</p>
                <p className="text-xs text-gray-400">{cat.slug}</p>
              </div>
            </div>
            <button
              onClick={() => {
                if (confirm(t('Supprimer la catégorie « {label} » ?', { label: cat.label_fr }))) del.mutate(cat.id);
              }}
              className="text-xs text-red-600 hover:underline"
            >
              {t('Supprimer')}
            </button>
          </div>
        ))}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <p className="text-sm font-medium text-gray-700 mb-2">{t('Ajouter une catégorie')}</p>
        <div className="grid grid-cols-[80px_120px_1fr_auto] gap-2 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('Icône')}</label>
            <input
              type="text"
              value={newIcon}
              onChange={(e) => setNewIcon(e.target.value)}
              placeholder="🎂"
              className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm text-center"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('Slug')}</label>
            <input
              type="text"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="birthday"
              className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('Libellé FR')}</label>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder={t('Anniversaire')}
              className="w-full px-2 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
          <Button
            size="sm"
            onClick={() => create.mutate()}
            disabled={!newSlug.trim() || !newLabel.trim()}
            loading={create.isPending}
          >
            {t('Ajouter')}
          </Button>
        </div>
      </div>
    </Section>
  );
}

// ─── Coût de revient (parametric cost model) ────────────────────────────────
// Every cost/yield is editable here — nothing hardcoded. Feeds computeAlbumCost
// (worker) → the profitability card, accounting COGS, and Customers contribution.
const COST_FIELDS: Array<{ key: string; label: string; def: number; step: number; unit?: string }> = [
  { key: 'print_sheet_price', label: 'Coût A3 double-face', def: 9, step: 0.5, unit: 'MAD' },
  { key: 'pages_per_a3', label: 'Pages par A3', def: 4, step: 1 },
  { key: 'branding_pages', label: 'Pages de marque (incluses)', def: 2, step: 1 },
  { key: 'print_cover_price', label: 'Couverture vinyle', def: 35, step: 1, unit: 'MAD' },
  { key: 'rigid_plaque_cost', label: 'Coût plaque rigide', def: 15, step: 1, unit: 'MAD' },
  { key: 'boards_per_plaque', label: 'Cartons par plaque', def: 10, step: 1 },
  { key: 'boards_per_book', label: 'Cartons par livre', def: 4, step: 1 },
  { key: 'spine_cost', label: 'Dos (tranche)', def: 0, step: 0.5, unit: 'MAD' },
  { key: 'glue_cost', label: 'Colle / épingles', def: 0.5, step: 0.5, unit: 'MAD' },
  { key: 'packaging_cost', label: 'Emballage (par commande)', def: 10.65, step: 0.05, unit: 'MAD' },
];

function CostParamsSection({ settings }: { settings: any }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const initial = () => Object.fromEntries(COST_FIELDS.map((f) => [f.key, Number(settings?.[f.key] ?? f.def)]));
  const [vals, setVals] = useState<Record<string, number>>(initial);
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (settings) setVals(initial()); /* eslint-disable-next-line */ }, [settings]);

  const save = useMutation({
    mutationFn: () => settingsApi.update(vals),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const pageUnit = vals.pages_per_a3 > 0 ? vals.print_sheet_price / vals.pages_per_a3 : 0;
  const boardUnit = vals.boards_per_plaque > 0 ? vals.rigid_plaque_cost / vals.boards_per_plaque : 0;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  // Live preview for a Solo book at the base page allowance we sell today
  // (BASE_PAGES_PER_ALBUM). Not order-specific — this is a settings preview.
  const interior = r2((BASE_PAGES_PER_ALBUM + vals.branding_pages) * pageUnit);
  const matieres = r2(vals.boards_per_book * boardUnit + vals.spine_cost + vals.glue_cost);

  return (
    <Section title={t('Coût de revient')} description={t('Paramètres matières + impression — tout est éditable, rien n’est codé en dur. Alimente la rentabilité, la compta et les clients.')}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {COST_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t(f.label)}</label>
            <div className="relative">
              <input
                type="number"
                min={0}
                step={f.step}
                value={vals[f.key]}
                onChange={(e) => setVals((p) => ({ ...p, [f.key]: parseFloat(e.target.value) || 0 }))}
                className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm ${f.unit ? 'pr-14' : ''}`}
              />
              {f.unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{f.unit}</span>}
            </div>
          </div>
        ))}
      </div>
      <div className="text-xs text-gray-500 mb-4 rounded-lg bg-gray-50 p-3 space-y-0.5">
        <p>{t('Coût/page intérieur = {u} MAD', { u: r2(pageUnit) })} · {t('Coût/carton = {u} MAD', { u: r2(boardUnit) })}</p>
        <p>{t('Aperçu livre 50 p. : Intérieur {i} · Couverture {c} · Matières {m} · Emballage {e}', { i: interior, c: vals.print_cover_price, m: matieres, e: vals.packaging_cost })}</p>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white disabled:opacity-60"
        >
          {save.isPending ? t('Enregistrement…') : t('Enregistrer')}
        </button>
        {saved && <span className="text-xs text-green-600">{t('✓ Enregistré')}</span>}
      </div>
    </Section>
  );
}

// ─── Album Settings ───────────────────────────────────────────────────────────

// ─── Tunnel de commande (funnel v2 toggle, 2026-07-22) ───────────────────────
// ONE switch, site-wide, no deploy needed. OFF (default) = classic 8-step
// funnel. ON = simplifié : COD → couverture → checkout storefront (upsells) →
// merci (CTA WhatsApp + pixel Purchase) ; photos envoyées APRÈS via WhatsApp.
function FunnelSection({ settings }: { settings: any }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState<boolean>(!!settings?.funnel_v2_enabled);
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (settings) setEnabled(!!settings.funnel_v2_enabled); }, [settings]);
  const save = useMutation({
    mutationFn: (next: boolean) => settingsApi.update({ funnel_v2_enabled: next } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['app-settings'] });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    },
  });
  return (
    <Section title={t('Tunnel de commande')} description={t('Change le parcours de TOUS les clients, instantanément — aucun déploiement nécessaire.')}>
      <div className="space-y-3">
        {([
          { v: false, title: t('Classique (par défaut)'), desc: t('Formulaire COD → couverture → photos → IA/manuel → aperçu → validation → checkout → merci') },
          { v: true,  title: t('Simplifié ⚡'), desc: t('Formulaire COD → couverture → checkout (avec upsells) → merci avec bouton WhatsApp. Le client envoie ses photos APRÈS, sur WhatsApp.') },
        ]).map((opt) => (
          <label key={String(opt.v)} className="flex items-start gap-3 p-3 rounded-xl border cursor-pointer"
            style={{ borderColor: enabled === opt.v ? '#C9A24A' : 'var(--color-adm-line)', background: enabled === opt.v ? '#C9A24A0D' : 'var(--color-adm-surface)' }}>
            <input type="radio" name="funnel" checked={enabled === opt.v}
              onChange={() => { setEnabled(opt.v); save.mutate(opt.v); }} className="mt-1 accent-[#C9A24A]" />
            <span>
              <span className="block text-sm font-semibold text-gray-800">{opt.title}</span>
              <span className="block text-xs text-gray-500 mt-0.5">{opt.desc}</span>
            </span>
          </label>
        ))}
        {save.isPending && <p className="text-xs text-gray-400">{t('Enregistrement…')}</p>}
        {saved && <p className="text-xs font-semibold text-green-600">✓ {t('Tunnel mis à jour — effet immédiat pour les nouveaux clients')}</p>}
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
      </div>
    </Section>
  );
}

// ─── File d'attente des agents (2026-08-11) ─────────────────────────────────
// The hold cap ships OFF: with a single Confirmation Agent a limit can only get
// in her way. Both values live here so they can be tuned when more agents join
// WITHOUT a deploy. Auto-release runs whether or not the cap is on — an
// abandoned order should come back to the queue either way.
function AssignmentSection({ settings }: { settings: any }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [capEnabled, setCapEnabled] = useState(settings?.agent_hold_cap_enabled ?? false);
  const [capLimit, setCapLimit] = useState(settings?.agent_hold_cap ?? 5);
  const [releaseHours, setReleaseHours] = useState(settings?.assignment_release_hours ?? 4);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) {
      setCapEnabled(settings.agent_hold_cap_enabled ?? false);
      setCapLimit(settings.agent_hold_cap ?? 5);
      setReleaseHours(settings.assignment_release_hours ?? 4);
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () => settingsApi.update({
      agent_hold_cap_enabled: capEnabled,
      agent_hold_cap: capLimit,
      assignment_release_hours: releaseHours,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  return (
    <Section
      title={t('File d’attente des agents')}
      description={t('Les nouvelles commandes arrivent dans une file partagée. L’agent clique « Prendre en charge » et la commande disparaît de la file des autres.')}
    >
      <label className="flex items-start gap-3 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={capEnabled}
          onChange={(e) => setCapEnabled(e.target.checked)}
          className="mt-0.5 w-4 h-4"
        />
        <span>
          <span className="block text-sm font-medium text-gray-800">
            {t('Limiter le nombre de commandes par agent')}
          </span>
          <span className="block text-xs text-gray-400 mt-0.5">
            {t('Désactivé : un agent peut prendre autant de commandes qu’il veut. À activer quand vous aurez plusieurs agents.')}
          </span>
        </span>
      </label>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('Maximum de commandes en cours')}
          </label>
          <input
            type="number"
            min={1}
            step={1}
            value={capLimit}
            disabled={!capEnabled}
            onChange={(e) => setCapLimit(parseInt(e.target.value, 10) || 1)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-400"
          />
          <p className="text-xs text-gray-400 mt-1">
            {t('Les commandes déjà confirmées ne comptent pas.')}
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('Remise en file après (heures)')}
          </label>
          <input
            type="number"
            min={0}
            step={1}
            value={releaseHours}
            onChange={(e) => setReleaseHours(parseInt(e.target.value, 10) || 0)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">
            {t('Sans action de l’agent pendant ce temps, la commande retourne dans la file. 0 = jamais.')}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={() => save.mutate()} loading={save.isPending} size="sm">
          {t('Enregistrer')}
        </Button>
        {saved && <p className="text-xs font-semibold text-green-600">✓ {t('Enregistré')}</p>}
        {save.isError && <p className="text-xs text-red-600">{(save.error as Error).message}</p>}
      </div>
    </Section>
  );
}

function AlbumSettingsSection({ settings }: { settings: any }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [pageCount, setPageCount] = useState(settings?.default_page_count ?? 50);
  const [pricePerPage, setPricePerPage] = useState(settings?.price_per_page ?? 0);
  const [costPerBook, setCostPerBook] = useState(settings?.cost_per_book ?? 0);
  const [advanceAmount, setAdvanceAmount] = useState(settings?.advance_amount ?? 50);
  const [agentRate, setAgentRate] = useState(settings?.agent_commission_rate ?? 0);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) {
      setPageCount(settings.default_page_count);
      setPricePerPage(settings.price_per_page ?? 0);
      setCostPerBook(settings.cost_per_book ?? 0);
      setAdvanceAmount(settings.advance_amount ?? 50);
      setAgentRate(settings.agent_commission_rate ?? 0);
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      settingsApi.update({
        default_page_count: pageCount,
        price_per_page: pricePerPage,
        cost_per_book: costPerBook,
        advance_amount: advanceAmount,
        agent_commission_rate: agentRate,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const papers = Math.ceil(pageCount / 2);

  return (
    <Section title={t('Album & Tarification')} description={t('Pages par défaut, prix de vente, coût de revient et acompte client')}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('Pages par défaut')}
          </label>
          <input
            type="number"
            min={2}
            step={2}
            value={pageCount}
            onChange={(e) => {
              const v = parseInt(e.target.value) || 50;
              setPageCount(v % 2 === 0 ? v : v + 1);
            }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">
            {t('= {papers} feuilles recto/verso', { papers })}
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('Prix page supp. (MAD)')}
          </label>
          <div className="relative">
            <input
              type="number"
              min={0}
              step={1}
              value={pricePerPage}
              onChange={(e) => setPricePerPage(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm pr-14"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">MAD</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">{t('Facturé au-delà de {pageCount} pages', { pageCount })}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('Commission agent / commande (MAD)')}
          </label>
          <div className="relative">
            <input
              type="number"
              min={0}
              step={1}
              value={agentRate}
              onChange={(e) => setAgentRate(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm pr-14"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">MAD</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {t('Verrouillée par commande au moment de la confirmation — un changement n’affecte jamais l’historique')}
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('Coût de revient / livre (MAD)')}
          </label>
          <div className="relative">
            <input
              type="number"
              min={0}
              step={1}
              value={costPerBook}
              onChange={(e) => setCostPerBook(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm pr-14"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">MAD</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">{t('Utilisé pour calculer le bénéfice')}</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('Acompte client (MAD)')}
          </label>
          <div className="relative">
            <input
              type="number"
              min={0}
              step={1}
              value={advanceAmount}
              onChange={(e) => setAdvanceAmount(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm pr-14"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">MAD</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">{t('Payé par virement avant livraison ; déduit du COD AMEEX')}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
          {t('Enregistrer')}
        </Button>
        {saved && (
          <span className="text-xs text-green-600 font-medium">{t('Sauvegardé !')}</span>
        )}
      </div>
    </Section>
  );
}

// ─── Print Settings ───────────────────────────────────────────────────────────

const PRINT_PRESETS = {
  konica_bizhub:  { printFormat: 'A3', dpi: 150, bleed: 0,  cropMarks: false },
  konica_accurio: { printFormat: 'A3', dpi: 300, bleed: 3,  cropMarks: true  },
  epson_l1850:    { printFormat: 'A3', dpi: 300, bleed: 0,  cropMarks: false },
  a4_standard:    { printFormat: 'A4', dpi: 150, bleed: 0,  cropMarks: false },
} as const;

// ─── AMEEX Defaults ──────────────────────────────────────────────────────────
// Phase 1 of the AMEEX auto-fill feature. The admin fills these once and
// every new parcel created from /admin/orders/<id> pre-fills with these
// values, so the end-of-day workflow never asks for "Nature de produit",
// "Fragile", or the opening/trying toggles per order.
function AmeexDefaultsSection({ settings }: { settings: any }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [product, setProduct] = useState(settings?.ameex_default_product ?? 'Album photo personnalisé');
  const [fragile, setFragile] = useState<boolean>(settings?.ameex_default_fragile ?? true);
  const [openAllowed, setOpenAllowed] = useState<boolean>(settings?.ameex_default_open ?? false);
  const [tryAllowed, setTryAllowed] = useState<boolean>(settings?.ameex_default_try ?? false);
  const [labelType, setLabelType] = useState<string>(settings?.ameex_default_label_type ?? 'Label_100_100');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setProduct(settings.ameex_default_product ?? 'Album photo personnalisé');
    setFragile(settings.ameex_default_fragile ?? true);
    setOpenAllowed(settings.ameex_default_open ?? false);
    setTryAllowed(settings.ameex_default_try ?? false);
    setLabelType(settings.ameex_default_label_type ?? 'Label_100_100');
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      settingsApi.update({
        ameex_default_product: product,
        ameex_default_fragile: fragile,
        ameex_default_open: openAllowed,
        ameex_default_try: tryAllowed,
        ameex_default_label_type: labelType,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const Toggle = ({ label, sub, value, onChange }: {
    label: string; sub?: string; value: boolean; onChange: (v: boolean) => void;
  }) => (
    <label className="flex items-start gap-3 cursor-pointer">
      <span
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${value ? 'bg-amber-500' : 'bg-gray-300'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-4' : 'translate-x-0.5'}`} />
      </span>
      <span className="text-sm">
        <span className="font-medium text-gray-800">{label}</span>
        {sub && <span className="block text-xs text-gray-500 mt-0.5">{sub}</span>}
      </span>
    </label>
  );

  return (
    <Section
      title={t('Livraison AMEEX — valeurs par défaut')}
      description={t('Pré-remplit le formulaire de colis pour chaque commande. Modifiable par commande si besoin.')}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('Nature de produit (champ « product » envoyé à AMEEX)')}
          </label>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            placeholder="Album photo personnalisé"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Toggle
            label={t('Fragile par défaut')}
            sub={t('Coche la case « Fragile » sur AMEEX (recommandé pour les albums)')}
            value={fragile}
            onChange={setFragile}
          />
          <Toggle
            label={t("Autoriser l'ouverture par le client")}
            sub={t("Si le colis est refusé, des frais de refus s'appliquent — laisser désactivé")}
            value={openAllowed}
            onChange={setOpenAllowed}
          />
          <Toggle
            label={t("Autoriser l'essayage par le client")}
            sub={t('Inutile pour un album imprimé — laisser désactivé')}
            value={tryAllowed}
            onChange={setTryAllowed}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t("Format d'étiquette d'impression batch")}
          </label>
          <select
            value={labelType}
            onChange={(e) => setLabelType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          >
            <option value="Label_100_100">{t('Thermique 10×10 cm (Label_100_100)')}</option>
            <option value="Label_A4">{t('A4 — une étiquette par page')}</option>
            <option value="Label_A4_8">{t('A4 — 8 étiquettes par page')}</option>
          </select>
          <p className="text-xs text-gray-400 mt-1">
            {t('Utilisé par le bouton « Confirmer + imprimer tout » du tableau Livraison.')}
          </p>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg disabled:opacity-50"
          >
            {save.isPending ? t('Enregistrement…') : t('Enregistrer')}
          </button>
          {saved && <span className="text-xs text-green-600">{t('✓ Enregistré')}</span>}
          {save.isError && <span className="text-xs text-red-600">{t('Échec — voir console')}</span>}
        </div>
      </div>
    </Section>
  );
}

function PrintSettingsSection({ settings }: { settings: any }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [preset, setPreset]           = useState<string>(settings?.printer_preset ?? 'konica_bizhub');
  const [printFormat, setPrintFormat] = useState<string>(settings?.print_format ?? 'A3');
  const [dpi, setDpi]                 = useState<number>(settings?.pdf_dpi ?? 150);
  const [bleed, setBleed]             = useState<number>(settings?.pdf_bleed_mm ?? 3);
  const [cropMarks, setCropMarks]     = useState<boolean>(settings?.pdf_crop_marks ?? false);
  const [saved, setSaved]             = useState(false);

  useEffect(() => {
    if (settings) {
      setPreset(settings.printer_preset ?? 'konica_bizhub');
      setPrintFormat(settings.print_format ?? 'A3');
      setDpi(settings.pdf_dpi ?? 150);
      setBleed(settings.pdf_bleed_mm ?? 3);
      setCropMarks(settings.pdf_crop_marks ?? false);
    }
  }, [settings]);

  function handlePresetChange(p: string) {
    setPreset(p);
    if (p !== 'custom' && p in PRINT_PRESETS) {
      const vals = PRINT_PRESETS[p as keyof typeof PRINT_PRESETS];
      setPrintFormat(vals.printFormat);
      setDpi(vals.dpi);
      setBleed(vals.bleed);
      setCropMarks(vals.cropMarks);
    }
  }

  const save = useMutation({
    mutationFn: () =>
      settingsApi.update({
        printer_preset: preset as AppSettings['printer_preset'],
        print_format:   printFormat as AppSettings['print_format'],
        pdf_dpi:        dpi,
        pdf_bleed_mm:   bleed,
        pdf_crop_marks: cropMarks,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  return (
    <Section title={t("Paramètres d'impression")} description={t('Imprimante et réglages PDF pour la génération')}>
      {/* 1. Printer Preset */}
      <div className="mb-4">
        <label className="block text-xs font-medium text-gray-700 mb-1">{t('Imprimante')}</label>
        <select
          value={preset}
          onChange={(e) => handlePresetChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="konica_bizhub">{t('Konica Minolta bizhub (bureau, A3 laser)')}</option>
          <option value="konica_accurio">{t('Konica Minolta AccurioPress (pro, SRA3)')}</option>
          <option value="epson_l1850">{t("Epson L1850 / L18050 (photo, A3 jet d'encre)")}</option>
          <option value="a4_standard">{t('Imprimante A4 standard')}</option>
          <option value="custom">{t('Personnalisé')}</option>
        </select>
        <p className="text-xs text-gray-400 mt-1">
          {t('Sélectionner une imprimante configure automatiquement les réglages ci-dessous.')}
        </p>
      </div>

      {/* 2. Individual overrideable settings */}
      <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-gray-100 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('Format du PDF')}</label>
          <select
            value={printFormat}
            onChange={(e) => { setPreset('custom'); setPrintFormat(e.target.value); }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="A4">{t('A4 — une page par feuille')}</option>
            <option value="A3">{t('A3 — deux pages par feuille (reliure à plat)')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('Résolution images (DPI)')}</label>
          <select
            value={dpi}
            onChange={(e) => { setPreset('custom'); setDpi(Number(e.target.value)); }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value={150}>{t('150 DPI — rapide, bureau')}</option>
            <option value={300}>{t('300 DPI — haute qualité, photo')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('Fond perdu (mm)')}</label>
          <input
            type="number"
            step="0.5"
            min="0"
            value={bleed}
            onChange={(e) => { setPreset('custom'); setBleed(parseFloat(e.target.value) || 0); }}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">{t('0 = sans fond perdu')}</p>
        </div>
        <div className="flex items-center gap-3 pt-4">
          <input
            type="checkbox"
            id="cropmarks"
            checked={cropMarks}
            onChange={(e) => { setPreset('custom'); setCropMarks(e.target.checked); }}
            className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
          />
          <label htmlFor="cropmarks" className="text-sm text-gray-700 cursor-pointer">
            {t('Traits de coupe')}
          </label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
          {t('Enregistrer')}
        </Button>
        {saved && (
          <span className="text-xs text-green-600 font-medium">{t('Sauvegardé !')}</span>
        )}
      </div>
    </Section>
  );
}

// ─── Assembly Template (fiche de montage) — rigid hardcover board placement ───
// A SEPARATE downloadable sheet (NEVER printed on the cover) the bindery uses
// to position the rigid boards on the printed vinyl wrap. Dimensions are global
// (single app_settings row) so editing here applies to EVERY order's fiche —
// past and future. The fiche itself is generated per order on the order page.
function AssemblyTemplateSection({ settings }: { settings: AppSettings | undefined }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<PrintSettings>({ ...DEFAULT_PRINT_SETTINGS, ...(settings?.print_template_settings ?? {}) });
  const [saved, setSaved] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    if (settings?.print_template_settings) setForm({ ...DEFAULT_PRINT_SETTINGS, ...settings.print_template_settings });
  }, [settings]);

  // The vinyl sheet is DERIVED from the case spec — recompute it from the live
  // form so the centring preview + saved value always follow the formula.
  const dForm = withDerivedVinyl(form);
  const vinyl = computeVinylSizeMm(form);
  const layout = computeTemplateLayout(dForm);
  const cm = (mm: number) => (mm / 10).toFixed(2).replace(/\.?0+$/, '').replace('.', ',');

  const save = useMutation({
    mutationFn: () => settingsApi.update({ print_template_settings: dForm }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  async function preview() {
    setPreviewing(true);
    try {
      const bytes = await generateAssemblyTemplatePdf(form);
      const blob = new Blob([bytes as BlobPart], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally {
      setPreviewing(false);
    }
  }

  // The vinyl sheet is computed from these inputs (computeVinylSizeMm) — it is
  // NOT edited directly, so it's shown read-only below instead of as a field.
  const fields: Array<[keyof PrintSettings, string]> = [
    ['boardWmm', t('Carton — largeur (mm)')],
    ['boardHmm', t('Carton — hauteur (mm)')],
    ['spineWmm', t('Dos — largeur (mm)')],
    ['jointGapMm', t('Jeu de charnière (mm)')],
    ['turnInMm', t('Rempli / turn-in (mm)')],
    ['edgeMm', t('Épaisseur de chant (mm)')],
    ['safeZoneMm', t('Zone de sécurité (mm)')],
    ['boardThicknessMm', t('Épaisseur carton (mm)')],
  ];

  return (
    <Section
      title={t('Fiche de montage — couverture rigide')}
      description={t("Dimensions des cartons rigides. La fiche de montage (téléchargeable sur chaque commande) n'est jamais imprimée sur la couverture. Réglage global : s'applique à toutes les commandes, anciennes et nouvelles.")}
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {fields.map(([k, label]) => (
          <div key={k}>
            <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
            <input
              type="number"
              step="0.5"
              min="0"
              value={form[k]}
              onChange={(e) => setForm((f) => ({ ...f, [k]: parseFloat(e.target.value) || 0 }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            />
          </div>
        ))}
      </div>

      <div className="rounded-lg p-3 mb-4 text-xs bg-gray-50 text-gray-600">
        {t('Taille du vinyle calculée :')} <b>{vinyl.wmm} × {vinyl.hmm} mm</b>{' '}
        <span className="text-gray-400">{t('(= 2·carton + 2·jeu + dos + 2·chant + 2·rempli + 2·zone de sécurité)')}</span>. {t('Recalculée automatiquement si le dos ou la zone de sécurité change.')}
        <br />
        {t('Centrage :')} <b>{cm(layout.turnInXmm)} cm</b> {t('gauche/droite,')} <b>{cm(layout.turnInYmm)} cm</b> {t('haut/bas. Dos centré, jeu de')} <b>{cm(form.jointGapMm)} cm</b> {t('de chaque côté.')}
      </div>

      <div className="flex items-center gap-3">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>{t('Enregistrer')}</Button>
        <Button size="sm" variant="secondary" onClick={preview} loading={previewing}>{t('Aperçu de la fiche')}</Button>
        {saved && <span className="text-xs text-green-600 font-medium">{t('Sauvegardé !')}</span>}
      </div>
    </Section>
  );
}

// ─── File Retention Section ───────────────────────────────────────────────────

// ─── Sécurité & Blocage (2026-07-08 — fraud guard) ──────────────────────────
// Blocklist (phones + IPs, checked by /orders/precheck before any COD draft is
// created) + the per-IP daily order cap. Entries also arrive via the order
// page's "Bannir ce client" button. Blocked people see a generic error at the
// COD form — never a "banned" signal.
function BlocklistSection({ settings }: { settings: any }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['blocklist'],
    queryFn: () => ordersApi.blocklist.list(),
  });
  const entries = data?.entries ?? [];

  const [kind, setKind] = useState<'phone' | 'ip'>('phone');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const add = useMutation({
    mutationFn: () => ordersApi.blocklist.add(kind, value.trim(), note.trim() || undefined),
    onSuccess: () => {
      setValue(''); setNote('');
      queryClient.invalidateQueries({ queryKey: ['blocklist'] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => ordersApi.blocklist.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['blocklist'] }),
  });

  const [ipCap, setIpCap] = useState<number>(settings?.max_orders_per_ip_per_day ?? 4);
  const [capSaved, setCapSaved] = useState(false);
  useEffect(() => {
    if (settings) setIpCap(settings.max_orders_per_ip_per_day ?? 4);
  }, [settings]);
  const saveCap = useMutation({
    mutationFn: () => settingsApi.update({ max_orders_per_ip_per_day: ipCap }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setCapSaved(true);
      setTimeout(() => setCapSaved(false), 2000);
    },
  });

  return (
    <Section title={t('Sécurité & Blocage')} description={t('Bloquer des clients (téléphone / IP) et limiter les commandes par IP. Les personnes bloquées voient une erreur générique — jamais un message de bannissement.')}>
      {/* Per-IP daily cap */}
      <div className="flex items-end gap-3 mb-5 pb-5 border-b border-gray-100">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('Commandes max par IP / 24h (0 = désactivé)')}</label>
          <input
            type="number"
            min={0}
            max={50}
            value={ipCap}
            onChange={(e) => setIpCap(Math.max(0, parseInt(e.target.value, 10) || 0))}
            className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-base md:text-sm"
          />
        </div>
        <Button size="sm" onClick={() => saveCap.mutate()} loading={saveCap.isPending} disabled={ipCap === (settings?.max_orders_per_ip_per_day ?? 4)}>
          {t('Enregistrer')}
        </Button>
        {capSaved && <span className="text-xs text-green-600 pb-2">✓</span>}
      </div>

      {/* Add entry */}
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('Type')}</label>
          <select value={kind} onChange={(e) => setKind(e.target.value as 'phone' | 'ip')} className="px-3 py-2 border border-gray-200 rounded-lg text-base md:text-sm">
            <option value="phone">{t('Téléphone')}</option>
            <option value="ip">IP</option>
          </select>
        </div>
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[160px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">{kind === 'phone' ? t('Numéro (06… / 212…)') : t('Adresse IP')}</label>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={kind === 'phone' ? '0612345678' : '105.66.x.x'}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-base md:text-sm font-mono"
          />
        </div>
        <div className="w-full sm:w-auto sm:flex-1 sm:min-w-[140px]">
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('Note (optionnel)')}</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('ex: fausses commandes #D275')}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-base md:text-sm"
          />
        </div>
        <Button size="sm" onClick={() => add.mutate()} loading={add.isPending} disabled={!value.trim()}>
          {t('🚫 Bloquer')}
        </Button>
      </div>
      {add.isError && <p className="text-xs text-red-600 mb-3">{(add.error as Error)?.message ?? t('Échec')}</p>}

      {/* Entries */}
      {entries.length === 0 ? (
        <p className="text-sm text-gray-400">{t('Aucun client bloqué.')}</p>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
          {entries.map((e) => (
            <div key={e.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">
                {e.kind === 'phone' ? '📞' : 'IP'}
              </span>
              <span className="font-mono text-xs">{e.value}</span>
              {e.note && <span className="text-xs text-gray-400 truncate flex-1">{e.note}</span>}
              {!e.note && <span className="flex-1" />}
              <span className="text-[10px] text-gray-400 shrink-0">{new Date(e.created_at).toLocaleDateString('fr-FR')}</span>
              <button
                type="button"
                onClick={() => { if (window.confirm(t('Débloquer {v} ?', { v: e.value }))) remove.mutate(e.id); }}
                className="text-xs text-gray-400 hover:text-red-600 shrink-0"
                title={t('Débloquer')}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function FileRetentionSection({ settings }: { settings: any }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [photoTrigger, setPhotoTrigger] = useState<string>(settings?.photo_retention_trigger ?? 'after_delivery');
  const [photoHours, setPhotoHours] = useState<number>(settings?.photo_retention_hours ?? 72);
  const [pdfTrigger, setPdfTrigger] = useState<string>(settings?.pdf_retention_trigger ?? 'manual');
  // Migration 280: X days after delivery, and the WhatsApp copies switch.
  const [deleteDays, setDeleteDays] = useState<number>(settings?.photo_delete_after_days ?? 14);
  const [copies, setCopies] = useState<string>(settings?.purge_whatsapp_copies ?? 'off');
  const [saved, setSaved] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [cleanResult, setCleanResult] = useState<CleanupOutcome | null>(null);
  // ⚠️ SEPARATE FROM `cleanResult`. The inline line stays after the popup is
  // dismissed — a figure you can go back and re-read beats one that only
  // existed for as long as you were looking at it.
  const [showCleanPopup, setShowCleanPopup] = useState(false);

  useEffect(() => {
    if (settings) {
      setPhotoTrigger(settings.photo_retention_trigger ?? 'after_delivery');
      setPhotoHours(settings.photo_retention_hours ?? 72);
      setPdfTrigger(settings.pdf_retention_trigger ?? 'manual');
      setDeleteDays(settings.photo_delete_after_days ?? 14);
      setCopies(settings.purge_whatsapp_copies ?? 'off');
    }
  }, [settings]);

  const save = useMutation({
    mutationFn: () => settingsApi.update({
      photo_retention_trigger: photoTrigger as AppSettings['photo_retention_trigger'],
      photo_retention_hours: photoHours,
      pdf_retention_trigger: pdfTrigger as AppSettings['pdf_retention_trigger'],
      photo_delete_after_days: Math.min(365, Math.max(1, deleteDays || 14)),
      purge_whatsapp_copies: copies as AppSettings['purge_whatsapp_copies'],
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  async function handleCleanupNow() {
    if (!confirm(t('Lancer le nettoyage maintenant ? Les fichiers supprimés ne peuvent pas être récupérés.'))) return;
    setCleaning(true);
    setCleanResult(null);
    try {
      const r = await ordersApi.runCleanup();
      const outcome = {
        photos_deleted: r.photos_deleted,
        pdfs_deleted: r.pdfs_deleted,
        bytes_freed: r.bytes_freed ?? 0,
        photos_remaining: r.photos_remaining ?? 0,
        pdfs_skipped_manual: r.pdfs_skipped_manual ?? 0,
      };
      setCleanResult(outcome);
      queryClient.invalidateQueries({ queryKey: ['cleanup-log'] });
      // Only when it earned it — see `didSomething`.
      setShowCleanPopup(didSomething(outcome));
    } catch (e) {
      alert(t('Erreur lors du nettoyage. Réessayez.'));
    } finally {
      setCleaning(false);
    }
  }

  return (
    <Section title={t('Rétention des fichiers')} description={t('Quand supprimer les photos et PDFs de Supabase Storage')}>
      <div className="grid sm:grid-cols-2 gap-5 mb-4">
        {/* Photo retention */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{t('📷 Photos clients')}</h4>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('Supprimer les photos')}</label>
            <select
              value={photoTrigger}
              onChange={(e) => setPhotoTrigger(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="manual">{t('Manuellement uniquement')}</option>
              <option value="after_upload_hours">{t("Après X heures suivant l'upload")}</option>
              <option value="after_delivery">{t('Après livraison de la commande')}</option>
            </select>
          </div>
          {photoTrigger === 'after_upload_hours' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('Délai avant suppression')}</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min={1} max={8760} step={1}
                  value={photoHours}
                  onChange={(e) => setPhotoHours(parseInt(e.target.value) || 72)}
                  className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <span className="text-sm text-gray-500">{t('heures')}</span>
                <span className="text-xs text-gray-400">{t('({days} jours)', { days: Math.round(photoHours / 24 * 10) / 10 })}</span>
              </div>
            </div>
          )}
          {photoTrigger === 'after_delivery' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('Supprimer après la livraison')}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min={1} max={365} step={1}
                    value={deleteDays}
                    onChange={(e) => setDeleteDays(parseInt(e.target.value) || 14)}
                    className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                  <span className="text-sm text-gray-500">{t('jours')}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">{t('Photos envoyées sur WhatsApp')}</label>
                <select
                  value={copies}
                  onChange={(e) => setCopies(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  <option value="off">{t('Garder (ne pas supprimer)')}</option>
                  <option value="dry_run">{t('Test : compter seulement, ne rien supprimer')}</option>
                  <option value="on">{t('Supprimer aussi')}</option>
                </select>
              </div>
              <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
                {t('Supprimées {days} jours après la livraison. Jamais pendant une réclamation ouverte, ni si le client demande de les garder.', { days: deleteDays })}
              </p>
              <p className="text-xs text-gray-500">
                {copies === 'on'
                  ? t("L'assistant peut promettre aux clients que leurs photos sont supprimées automatiquement.")
                  : t("L'assistant ne promet pas la suppression tant que les photos WhatsApp ne sont pas supprimées aussi.")}
              </p>
            </>
          )}
          {photoTrigger === 'manual' && (
            <p className="text-xs text-gray-400">{t('Les photos ne seront jamais supprimées automatiquement.')}</p>
          )}
        </div>

        {/* PDF retention */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">{t('📄 PDFs générés')}</h4>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('Supprimer les PDFs')}</label>
            <select
              value={pdfTrigger}
              onChange={(e) => setPdfTrigger(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="manual">{t('Manuellement uniquement')}</option>
              <option value="after_delivery">{t('Après livraison de la commande')}</option>
            </select>
          </div>
          {pdfTrigger === 'after_delivery' && (
            <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
              {t('PDFs supprimés automatiquement avec les photos lors du statut')} <strong>{t('Livré')}</strong>.
            </p>
          )}
          {pdfTrigger === 'manual' && (
            <p className="text-xs text-gray-400">{t('Les PDFs restent disponibles indéfiniment (liens valides 1 an).')}</p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
          {t('Enregistrer')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleCleanupNow}
          loading={cleaning}
          className="text-red-600 hover:bg-red-50 border border-red-200"
        >
          {t('🗑️ Nettoyer maintenant')}
        </Button>
        {saved && <span className="text-xs text-green-600 font-medium">{t('Sauvegardé !')}</span>}
        {cleanResult && (
          <div className="text-xs space-y-0.5">
            <span className="text-green-700 font-medium block">
              ✓ {freedDetail(cleanResult)}
              {cleanResult.bytes_freed > 0 && ` — ${formatFreed(cleanResult.bytes_freed).label} ${t('libérés')}`}
            </span>
            {/* ⚠️ A PARTIAL RUN MUST NOT READ AS A FINISHED ONE. */}
            {remainingNote(cleanResult) && (
              <span className="text-amber-700 font-medium block">{remainingNote(cleanResult)}</span>
            )}
            {/* ⚠️ AND A DELIBERATE ZERO MUST NOT READ AS A FAILURE. */}
            {pdfSkipNote(cleanResult) && (
              <span className="text-gray-500 block">{pdfSkipNote(cleanResult)}</span>
            )}
          </div>
        )}
      </div>

      {showCleanPopup && cleanResult && (
        <CleanupResultModal result={cleanResult} onClose={() => setShowCleanPopup(false)} />
      )}
      <DeletionLog />
      <div className="hidden">
      </div>
    </Section>
  );
}

// ─── Journal des suppressions (migration 280) ───────────────────────────────
function DeletionLog() {
  const { t } = useT();
  const { data } = useQuery({ queryKey: ['cleanup-log'], queryFn: () => ordersApi.cleanupLog(), staleTime: 60_000 });
  const rows = data?.rows ?? [];
  if (!rows.length) return null;
  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <h4 className="text-xs font-semibold text-gray-600 mb-2">{t('Dernières suppressions')}</h4>
      <ul className="space-y-1 text-xs text-gray-600 max-h-48 overflow-y-auto">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap gap-x-2">
            <span className="text-gray-400">{r.created_at.slice(0, 10)}</span>
            <span className="font-medium">{r.order_name || '—'}</span>
            <span>
              {r.dry_run ? t('test — {n} photos WhatsApp seraient supprimées', { n: r.whatsapp_copies })
                : t('{a} photos album · {w} photos WhatsApp supprimées', { a: r.album_photos, w: r.whatsapp_copies })}
            </span>
            {r.days_after_delivery != null && <span className="text-gray-400">{t('(J+{d})', { d: r.days_after_delivery })}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Imprimeur (send-to-printer contact + channel toggles) ───────────────────
// Email + WhatsApp are independent channels for the "Envoyer à l'imprimeur"
// bulk action on the orders list. Either, both, or neither may be enabled; when
// a channel is off the bulk action still produces the zip + direct download.
function PrinterSection({ settings }: { settings: AppSettings | undefined }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(settings?.printer_email ?? '');
  const [whatsapp, setWhatsapp] = useState(settings?.printer_whatsapp ?? '');
  const [emailEnabled, setEmailEnabled] = useState<boolean>(settings?.printer_email_enabled ?? false);
  const [whatsappEnabled, setWhatsappEnabled] = useState<boolean>(settings?.printer_whatsapp_enabled ?? false);
  // Rate card (Phase 2): defaults applied to every NEW printer batch — each
  // batch stays editable individually (bulk discounts).
  const [sheetPrice, setSheetPrice] = useState(String(settings?.print_sheet_price ?? 9));
  const [coverPrice, setCoverPrice] = useState(String(settings?.print_cover_price ?? 35));
  const [deliveryCost, setDeliveryCost] = useState(String(settings?.delivery_cost_default ?? 36));
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setEmail(settings.printer_email ?? '');
    setWhatsapp(settings.printer_whatsapp ?? '');
    setEmailEnabled(settings.printer_email_enabled ?? false);
    setWhatsappEnabled(settings.printer_whatsapp_enabled ?? false);
    setSheetPrice(String(settings.print_sheet_price ?? 9));
    setCoverPrice(String(settings.print_cover_price ?? 35));
    setDeliveryCost(String(settings.delivery_cost_default ?? 36));
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      settingsApi.update({
        printer_email: email.trim() || null,
        printer_whatsapp: whatsapp.trim() || null,
        printer_email_enabled: emailEnabled,
        printer_whatsapp_enabled: whatsappEnabled,
        print_sheet_price: Number(sheetPrice) || 9,
        print_cover_price: Number(coverPrice) || 35,
        delivery_cost_default: Number(deliveryCost) || 36,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  return (
    <Section
      title={t('Imprimeur')}
      description={t("Contact de l'imprimeur pour l'action « Envoyer à l'imprimeur » + tarifs par défaut des lots d'impression.")}
    >
      <div className="space-y-4">
        {/* Rate card — Phase 2 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-xl p-3" style={{ background: 'rgba(201,162,74,.06)', border: '1px solid rgba(201,162,74,.25)' }}>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('Prix / feuille A3 (MAD)')}</label>
            <input type="number" min="0" step="0.5" value={sheetPrice} onChange={(e) => setSheetPrice(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
            <p className="text-[11px] text-gray-400 mt-0.5">{t('70g recto-verso = 4 pages A4')}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('Prix / couverture (MAD)')}</label>
            <input type="number" min="0" step="0.5" value={coverPrice} onChange={(e) => setCoverPrice(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
            <p className="text-[11px] text-gray-400 mt-0.5">{t('Modifiable par lot (gros volume)')}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('Livraison forfait (MAD)')}</label>
            <input type="number" min="0" step="0.5" value={deliveryCost} onChange={(e) => setDeliveryCost(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500" />
            <p className="text-[11px] text-gray-400 mt-0.5">{t('Si AMEEX ne donne pas le vrai prix')}</p>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t("Email de l'imprimeur")}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="imprimeur@example.com"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <label className="flex items-center gap-2 text-xs text-gray-700 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={emailEnabled}
              onChange={(e) => setEmailEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
            />
            {t("Envoyer un email (lien de téléchargement du zip) à l'imprimeur")}
          </label>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">{t("WhatsApp de l'imprimeur")}</label>
          <input
            type="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="06XXXXXXXX ou 2126XXXXXXXX"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <label className="flex items-center gap-2 text-xs text-gray-700 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={whatsappEnabled}
              onChange={(e) => setWhatsappEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
            />
            {t("Ouvrir un lien WhatsApp pré-rempli vers l'imprimeur")}
          </label>
        </div>
      </div>

      <div className="flex items-center gap-3 pt-4 mt-4 border-t border-gray-100">
        <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
          {t('Enregistrer')}
        </Button>
        {saved && <span className="text-xs text-green-600 font-medium">{t('Sauvegardé !')}</span>}
      </div>
      <p className="text-xs text-gray-400 mt-3">
        {t("L'email part via MailChannels. Configurez les secrets du worker")}
        (<code>MAILCHANNELS_API_KEY</code>, <code>PRINTER_FROM_EMAIL</code>) {t("pour l'activer ; sans cela, l'action génère quand même le zip et le lien WhatsApp.")}
      </p>
    </Section>
  );
}

/** Night mode (2026-10-02). Per PERSON since 2026-10-03 (saved on the server,
 *  it follows them into the editor), not a studio setting: the same
 *  preference as « More › Apparence » (lib/theme/adminTheme.ts). */
function AppearanceSection() {
  const { t } = useT();
  return (
    <Section title={t('Apparence')} description={t('Clair, sombre, ou comme le téléphone. Enregistré pour votre compte : il vous suit partout, éditeur et aperçu compris. Les photos, les pages et les couvertures ne changent jamais de couleur.')}>
      <AppearanceSwitch className="max-w-sm" />
    </Section>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

// ─── Editor Resources — Fonts (Section 8) + Icons (Section 9) ────────────────
// Sub-tabbed admin section. Both sub-tabs hit the same /editor-resources
// endpoint family. Saved entries propagate to the customer editor at
// runtime — fonts via dynamic <link> injection, icons via the Elements
// panel grid. Delete is hard-delete (no soft-archive) since these are
// non-destructive catalogue items.
function EditorResourcesSection() {
  const { t } = useT();
  const [tab, setTab] = useState<'fonts' | 'icons'>('fonts');
  return (
    <Section title={t("Ressources de l'éditeur")} description={t("Polices et icônes disponibles dans l'éditeur d'album.")}>
      <div className="flex gap-1 mb-4 p-1 rounded-lg bg-gray-50 border border-gray-100 w-fit">
        {(['fonts', 'icons'] as const).map((tab2) => (
          <button
            key={tab2}
            onClick={() => setTab(tab2)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${tab === tab2
              ? 'bg-[#1F2937] text-white dark:bg-adm-gold-wash dark:text-adm-gold-ink'
              : 'text-[#6B7280] dark:text-adm-muted'}`}
          >
            {tab2 === 'fonts' ? t('Polices') : t('Icônes et éléments')}
          </button>
        ))}
      </div>
      {tab === 'fonts' ? <EditorFontsTab /> : <EditorIconsTab />}
    </Section>
  );
}

function EditorFontsTab() {
  const { t } = useT();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['editor-fonts'],
    queryFn: () => editorResourcesApi.listFonts(),
  });
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addMut = useMutation({
    mutationFn: () => editorResourcesApi.addFont({ name: name.trim(), google_url: url.trim() }),
    onSuccess: () => {
      setName(''); setUrl(''); setError(null);
      qc.invalidateQueries({ queryKey: ['editor-fonts'] });
    },
    onError: (e: any) => setError(e?.message ?? t('Erreur')),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => editorResourcesApi.deleteFont(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['editor-fonts'] }),
  });

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs text-gray-500 mb-3">
          {t("Les polices ajoutées ici apparaissent dans le sélecteur du toolbar texte de l'éditeur. Utilisez l'URL")} <code className="px-1 py-0.5 bg-gray-100 rounded">https://fonts.googleapis.com/css2?family=…</code> {t('fournie par Google Fonts.')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('Nom (ex. Lobster)')}
            className="px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:border-gray-400"
          />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://fonts.googleapis.com/css2?family=Lobster&display=swap"
            className="px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:border-gray-400 font-mono"
          />
          <Button
            size="sm"
            onClick={() => addMut.mutate()}
            disabled={!name.trim() || !url.trim() || addMut.isPending}
          >
            {addMut.isPending ? t('Ajout…') : t('Ajouter')}
          </Button>
        </div>
        {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      </div>

      {/* List */}
      <div className="border border-gray-100 rounded-lg overflow-hidden">
        {isLoading ? (
          <p className="px-4 py-3 text-xs text-gray-400">{t('Chargement…')}</p>
        ) : (data?.fonts ?? []).length === 0 ? (
          <p className="px-4 py-3 text-xs text-gray-400">{t('Aucune police personnalisée pour le moment.')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data!.fonts.map((f) => (
              <li key={f.id} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                  <p className="text-[11px] text-gray-500 truncate font-mono">{f.google_url}</p>
                </div>
                <button
                  onClick={() => { if (confirm(t('Supprimer « {name} » ?', { name: f.name }))) delMut.mutate(f.id); }}
                  className="text-xs text-red-600 hover:underline shrink-0"
                  disabled={delMut.isPending}
                >
                  {t('Supprimer')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function EditorIconsTab() {
  const { t } = useT();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['editor-icons'],
    queryFn: () => editorResourcesApi.listIcons(),
  });
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [svgText, setSvgText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const addMut = useMutation({
    mutationFn: () => editorResourcesApi.addIcon({
      name: name.trim(),
      svg: svgText.trim(),
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
    }),
    onSuccess: () => {
      setName(''); setSvgText(''); setTags(''); setError(null);
      qc.invalidateQueries({ queryKey: ['editor-icons'] });
    },
    onError: (e: any) => setError(e?.message ?? t('Erreur')),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => editorResourcesApi.deleteIcon(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['editor-icons'] }),
  });

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      setSvgText(text);
      if (!name) setName(file.name.replace(/\.svg$/i, ''));
    };
    reader.readAsText(file);
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        {t('Téléversez un fichier SVG ou collez son code. Les icônes sont disponibles dans le panneau "Éléments" de l\'éditeur client.')}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('Nom (ex. Coeur)')}
          className="px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:border-gray-400"
        />
        <input
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder={t('Étiquettes (séparées par virgules)')}
          className="px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:border-gray-400"
        />
      </div>
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept=".svg,image/svg+xml"
               onChange={(e) => handleFile(e.target.files?.[0])}
               className="hidden" />
        <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()}>{t('Téléverser SVG')}</Button>
        <span className="text-xs text-gray-500">{t('… ou collez le code ci-dessous')}</span>
      </div>
      <textarea
        value={svgText}
        onChange={(e) => setSvgText(e.target.value)}
        placeholder='<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">...</svg>'
        rows={4}
        className="w-full px-3 py-2 text-xs font-mono rounded-md border border-gray-200 focus:outline-none focus:border-gray-400"
      />
      <div className="flex items-center gap-3">
        <Button size="sm" onClick={() => addMut.mutate()} disabled={!name.trim() || !svgText.trim() || addMut.isPending}>
          {addMut.isPending ? t('Ajout…') : t("Ajouter l'icône")}
        </Button>
        {svgText && (
          <SafeSvg svg={svgText}
                className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center bg-white"
                title={t('Aperçu')} />
        )}
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}

      {/* List */}
      <div className="border border-gray-100 rounded-lg overflow-hidden">
        {isLoading ? (
          <p className="px-4 py-3 text-xs text-gray-400">{t('Chargement…')}</p>
        ) : (data?.icons ?? []).length === 0 ? (
          <p className="px-4 py-3 text-xs text-gray-400">{t('Aucune icône pour le moment.')}</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data!.icons.map((ic) => (
              <li key={ic.id} className="px-4 py-3 flex items-center gap-3">
                <SafeSvg svg={ic.svg}
                      className="w-8 h-8 rounded border border-gray-100 flex items-center justify-center bg-white shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{ic.name}</p>
                  {ic.tags.length > 0 && <p className="text-[11px] text-gray-500 truncate">{ic.tags.join(', ')}</p>}
                </div>
                <button
                  onClick={() => { if (confirm(t('Supprimer « {name} » ?', { name: ic.name }))) delMut.mutate(ic.id); }}
                  className="text-xs text-red-600 hover:underline shrink-0"
                  disabled={delMut.isPending}
                >
                  {t('Supprimer')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
